/* Add an ad hoc charge, complete its missing side, or change it.
   mode 'add'  : search and pick the AWB, charge type, KAM side and / or Ops side.
   mode 'kam'  : add or change the client side (amount, approval needed, approval file).
   mode 'ops'  : add or change the courier side (amount).
   The server checks the caller's PTL team for each side and every rule; messages are shown as they come. */
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem,
  Radio, RadioGroup, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import { useAdhocAwbSearchQuery, useAdhocTypesQuery, useCreateAdhocMutation, useUpdateAdhocMutation, useUploadAdhocProofMutation } from '../../../redux/ptlBillingApiSlice'
import { errText, fmtDate, inr } from '../billingUtils'

const OTHER = 'OTHER'

export default function AdhocChargeDialog({ mode = 'add', charge, presetAwb, onClose }) {
  const { enqueueSnackbar } = useSnackbar()
  const { data: types = [] } = useAdhocTypesQuery()
  const [create, { isLoading: creating }] = useCreateAdhocMutation()
  const [update, { isLoading: updating }] = useUpdateAdhocMutation()
  const [upload, { isLoading: uploading }] = useUploadAdhocProofMutation()

  const [q, setQ] = useState(presetAwb || '')
  const [debounced, setDebounced] = useState(presetAwb || '')
  const [picked, setPicked] = useState(null)
  const [type, setType] = useState(charge ? charge.chargeTypeCode?.split(':').pop() : '')
  const [otherDesc, setOtherDesc] = useState(charge?.otherDesc || '')
  const [client, setClient] = useState(charge?.clientAmount ?? '')
  const [approval, setApproval] = useState(charge?.approvalNeeded === false ? 'no' : 'yes')
  const [file, setFile] = useState(null)
  const [courier, setCourier] = useState(charge?.courierAmount ?? '')
  const [remarks, setRemarks] = useState(charge?.remarks || '')
  const [dupNeeded, setDupNeeded] = useState(false)
  const [allowDup, setAllowDup] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 300); return () => clearTimeout(t) }, [q])
  const { data: found = [], isFetching: searching } = useAdhocAwbSearchQuery(debounced, { skip: mode !== 'add' || debounced.length < 3 })
  useEffect(() => { // a preset AWB (from a shipment row) is picked as soon as the search finds it
    if (presetAwb && !picked && found.length) setPicked(found.find((f) => f.awb === presetAwb) || null)
  }, [found, presetAwb, picked])

  const showKam = mode === 'add' || mode === 'kam'
  const showOps = mode === 'add' || mode === 'ops'
  const showType = mode === 'add'
  const typeObj = useMemo(() => types.find((t) => t.code === type), [types, type])
  const busy = creating || updating || uploading
  // the client's wallet comes with the picked AWB (add) or the charge (change); only a prepaid wallet is debited
  const walletOf = mode === 'add' ? picked : charge
  const prepaid = walletOf?.walletType === 'Prepaid'
  const walletNote = !walletOf ? 'Pick the AWB to see whether the client is prepaid (wallet debited) or postpaid (bill only).'
    : prepaid ? `Prepaid client · wallet balance ${walletOf.walletBalance != null ? inr(walletOf.walletBalance) : '—'}. Exactly this amount is debited from the wallet when saved (no GST); an edit moves only the difference.`
      : walletOf.walletType === 'Postpaid' ? 'Postpaid client: billed on the monthly bill only, no wallet change.'
        : 'No wallet for this client: billed on the monthly bill only.'

  const save = async () => {
    setErr(null)
    let proofKey = null, proofFileName = null
    const kamSet = showKam && client !== ''
    // the client amount is what a prepaid client's wallet is debited (exactly, no GST): confirm it before saving
    const before = Number(charge?.clientAmount || 0)
    if (prepaid && kamSet && Number(client) !== before) {
      const change = Number(client) - before
      const balance = walletOf.walletBalance != null ? ` Wallet balance now ${inr(walletOf.walletBalance)}.` : ''
      const msg = before > 0
        ? `Client amount ${inr(before)} → ${inr(Number(client))}. The prepaid wallet is ${change > 0 ? `debited ${inr(change)} more` : `credited back ${inr(-change)}`}.${balance} Continue?`
        : `Client amount ${inr(Number(client))}. Exactly ${inr(Number(client))} is debited from the prepaid wallet now (no GST).${balance} Continue?`
      if (!window.confirm(msg)) return
    }
    if (kamSet && approval === 'yes') {
      if (file) {
        const up = await upload(file)
        if (up.error) return setErr(errText(up.error))
        proofKey = up.data.proofKey
        proofFileName = up.data.proofFileName
      } else if (!(mode === 'kam' && charge?.hasProof)) {
        return setErr("Attach the client's approval (screenshot, email or PDF), or choose 'approval not needed'.")
      }
    }
    const body = {}
    if (kamSet) Object.assign(body, { clientAmount: Number(client), approvalNeeded: approval === 'yes', proofKey, proofFileName })
    if (showOps && courier !== '') body.courierAmount = Number(courier)
    if (remarks !== (charge?.remarks || '')) body.remarks = remarks
    let res
    if (mode === 'add') {
      if (!picked) return setErr('Search and select the AWB first.')
      if (!type) return setErr('Pick a charge type.')
      res = await create({ ...body, awb: picked.awb, chargeTypeCode: type, otherDesc: type === OTHER ? otherDesc : null, allowDuplicate: allowDup })
    } else {
      res = await update({ id: charge.id, version: charge.version, ...body })
    }
    if (res.error) {
      const m = errText(res.error)
      if (/already added/i.test(m)) setDupNeeded(true)
      return setErr(m)
    }
    const st = res.data?.state
    enqueueSnackbar(st === 'WAITING_OPS' ? 'Saved. Waiting for Ops to add the courier cost.'
      : st === 'WAITING_KAM' ? 'Saved. Waiting for KAM to add the client amount.' : 'Saved. The charge is complete.', { variant: 'success' })
    onClose(true)
  }

  const title = mode === 'add' ? 'Add Ad hoc Charge' : mode === 'kam' ? 'Client side (KAM)' : 'Courier side (Ops)'
  return (
    <Dialog open onClose={() => onClose(false)} maxWidth='sm' fullWidth>
      <DialogTitle>
        {title}
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {mode === 'add' ? 'One AWB. Fill your side; the other team completes theirs.' : `${charge.chargeTypeLabel} · ${charge.awb} · ${charge.merchantName || ''}`}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {mode === 'add' && (
          <Box>
            <Autocomplete
              options={found}
              value={picked}
              inputValue={q}
              loading={searching}
              filterOptions={(x) => x}
              noOptionsText={q.trim().length < 3 ? 'Type at least 3 characters of the AWB' : 'No delivered or lost PTL shipment matches'}
              onInputChange={(_e, val, reason) => { if (reason !== 'reset') { setQ(val); setPicked(null) } }}
              onChange={(_e, o) => { setPicked(o); if (o) setQ(o.awb) }}
              getOptionLabel={(o) => o.awb}
              isOptionEqualToValue={(a, b) => a.orderOid === b.orderOid}
              getOptionDisabled={(o) => o.locked}
              renderOption={(props, o) => (
                <li {...props} key={o.orderOid}>
                  <Box sx={{ display: 'flex', gap: 1.5, width: 1, fontSize: 13 }}>
                    <b style={{ minWidth: 120 }}>{o.awb}</b><span>{o.merchantName}</span>
                    <span style={{ marginLeft: 'auto', color: '#637381' }}>{o.toCity} {o.toPincode}{o.locked ? ' · locked' : ''}</span>
                  </Box>
                </li>
              )}
              renderInput={(p) => <TextField {...p} size='small' label='Shipment (AWB)' placeholder='Type the AWB number' />}
            />
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
              {picked ? `${picked.awb} · ${picked.merchantName} · ${picked.courierName} · ${picked.toCity} ${picked.toPincode} · ${picked.status} ${fmtDate(picked.deliveredAt)}`
                + (picked.cycleMonth ? ` · billing ${picked.cycleMonth} (${picked.lineStatus})` : '') : 'No shipment selected yet.'}
            </Typography>
          </Box>
        )}

        {showType && (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: type === OTHER ? '1fr 1fr' : '1fr' }}>
            <TextField select size='small' label='Charge type' value={type} onChange={(e) => { setType(e.target.value); setDupNeeded(false); setAllowDup(false) }}>
              {types.map((t) => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
            </TextField>
            {type === OTHER && <TextField size='small' label='Describe the charge' value={otherDesc} onChange={(e) => setOtherDesc(e.target.value)} inputProps={{ maxLength: 200 }} />}
          </Box>
        )}

        {showKam && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, display: 'grid', gap: 1.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', color: 'text.secondary' }}>KAM · WHAT WE CHARGE THE CLIENT (EXCL. GST)</Typography>
            <TextField size='small' type='number' label='Client amount ₹ (agreed with the client)' value={client} onChange={(e) => setClient(e.target.value)} inputProps={{ min: 0, step: '0.01' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Client approval needed?</Typography>
              <RadioGroup row value={approval} onChange={(e) => setApproval(e.target.value)}>
                <FormControlLabel value='yes' control={<Radio size='small' />} label='Yes' />
                <FormControlLabel value='no' control={<Radio size='small' />} label='No' />
              </RadioGroup>
            </Box>
            {approval === 'yes' && (
              <Box>
                <Button variant='outlined' component='label' size='small' sx={{ textTransform: 'none' }}>
                  {file ? file.name : 'Attach approval (PNG, JPG or PDF)'}
                  <input hidden type='file' accept='image/png,image/jpeg,application/pdf' onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </Button>
                {mode === 'kam' && charge?.hasProof && !file && <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Current approval kept: {charge.proofFileName}</Typography>}
              </Box>
            )}
            <Typography sx={{ fontSize: 12, color: prepaid ? 'warning.dark' : 'text.secondary' }}>{walletNote}</Typography>
          </Box>
        )}

        {showOps && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, display: 'grid', gap: 1.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', color: 'text.secondary' }}>OPS · WHAT THE COURIER CHARGES US (EXCL. GST)</Typography>
            <TextField size='small' type='number' label='Courier amount ₹' value={courier} onChange={(e) => setCourier(e.target.value)} inputProps={{ min: 0, step: '0.01' }} />
            {mode === 'add' && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Leave blank if Ops will add it later.</Typography>}
          </Box>
        )}

        <TextField size='small' label='Remarks' value={remarks} onChange={(e) => setRemarks(e.target.value)} multiline minRows={2} inputProps={{ maxLength: 500 }}
          placeholder='e.g. Extra vehicle approved by client on call, screenshot attached' />
        {dupNeeded && <FormControlLabel control={<Checkbox checked={allowDup} onChange={(e) => setAllowDup(e.target.checked)} />} label={`Add another ${typeObj?.label || 'charge'} on this AWB anyway`} />}
        {err && <Alert severity='error'>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <LoadingButton variant='contained' loading={busy} onClick={save}>{mode === 'add' ? 'Add charge' : 'Save'}</LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
